/**
 * Cart Validation UI Module
 * Handles all user interface components for cart validation
 */

class CartValidationUI {
  constructor(config = {}) {
    this.config = {
      debug: config.debug || false,
      theme: {
        primaryColor: '#ff3da1',
        successColor: '#1a7f37',
        fontFamily: 'Montserrat, sans-serif',
        ...config.theme
      },
      ...config
    };
    
    this.activeNotifications = new Set();
    this.styleInjected = false;
  }

  /**
   * Show validation progress overlay
   */
  showValidationProgress() {
    this.injectStyles();
    
    const overlay = this.createProgressOverlay();
    document.body.appendChild(overlay);
    
    // Start progress sequence
    this.startProgressSequence(overlay);
    
    return overlay;
  }

  /**
   * Show validation completion and redirect
   */
  showValidationComplete(overlay, checkoutUrl, delay = 1500) {
    const content = overlay.querySelector('.cart-validation-content');
    
    content.innerHTML = `
      <div class="cart-validation-icon cart-validation-icon--success">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <h3 class="cart-validation-title">Cart Validated!</h3>
      <p class="cart-validation-message">Your cart has been updated</p>
      <p class="cart-validation-submessage">Redirecting to checkout...</p>
    `;

    setTimeout(() => {
      this.log('Redirecting to checkout');
      window.location.href = checkoutUrl;
    }, delay);
  }

  /**
   * Show checkout validation summary with removed items
   */
  showCheckoutValidationSummary(removedItems, checkoutUrl) {
    this.injectStyles();
    
    const overlay = this.createCheckoutSummaryOverlay(removedItems, checkoutUrl);
    document.body.appendChild(overlay);
    
    return overlay;
  }

  /**
   * Show notification about removed items
   */
  showRemovalNotification(removedItems) {
    if (!removedItems || removedItems.length === 0) return;

    this.injectStyles();
    
    const notification = this.createRemovalNotification(removedItems);
    document.body.appendChild(notification);
    this.activeNotifications.add(notification);

    // Auto-remove after delay
    setTimeout(() => {
      this.removeNotification(notification);
    }, 8000);

    return notification;
  }

  /**
   * Update cart icon/counter
   */
  updateCartIcon(cart) {
    // Update cart count bubble specifically (not the entire cart icon)
    const cartBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (cartBubble) {
      const spans = cartBubble.querySelectorAll('span');
      spans.forEach(span => {
        span.textContent = cart.item_count;
      });
      
      // Show/hide the entire bubble based on cart count
      cartBubble.style.display = cart.item_count === 0 ? 'none' : '';
    }

    // Update other cart count elements (but not the main cart icon)
    const otherSelectors = [
      '.cart-count:not(#cart-icon-bubble .cart-count)',
      '[data-cart-count]:not(#cart-icon-bubble)'
    ];

    otherSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.textContent = cart.item_count;
        element.style.display = cart.item_count === 0 ? 'none' : '';
      });
    });

    this.log('Updated cart icon with count:', cart.item_count);
  }

  /**
   * Create progress overlay element
   */
  createProgressOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'cart-validation-overlay';
    
    overlay.innerHTML = `
      <div class="cart-validation-modal">
        <div class="cart-validation-content">
          <div class="cart-validation-icon cart-validation-icon--loading">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M9 11l3 3l8-8"/>
              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9s4.03-9 9-9c1.44 0 2.8.34 4 .94"/>
            </svg>
          </div>
          <h3 class="cart-validation-title">Validating Your Cart</h3>
          <p class="cart-validation-message">We're checking for items you already own...</p>
          <p class="cart-validation-submessage">This will only take a moment</p>
          <div class="cart-validation-progress">Scanning cart items...</div>
        </div>
      </div>
    `;

    return overlay;
  }

  /**
   * Create removal notification element
   */
  createRemovalNotification(removedItems) {
    const notification = document.createElement('div');
    notification.className = 'cart-validation-notification';
    
    const message = this.buildRemovalMessage(removedItems);
    
    notification.innerHTML = `
      <div class="cart-validation-notification-content">
        <div class="cart-validation-notification-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <div class="cart-validation-notification-body">
          <div class="cart-validation-notification-title">Items Removed</div>
          <div class="cart-validation-notification-message">${message}</div>
        </div>
        <button class="cart-validation-notification-close" onclick="this.closest('.cart-validation-notification').remove()">×</button>
      </div>
    `;

    return notification;
  }

  /**
   * Create checkout summary overlay element
   */
  createCheckoutSummaryOverlay(removedItems, checkoutUrl) {
    const overlay = document.createElement('div');
    overlay.className = 'cart-validation-overlay';
    
    const itemsList = this.buildDetailedItemsList(removedItems);
    
    overlay.innerHTML = `
      <div class="cart-validation-modal cart-validation-modal--checkout">
        <div class="cart-validation-content">
          <div class="cart-validation-icon cart-validation-icon--warning">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <h3 class="cart-validation-title">Items Removed from Cart</h3>
          <p class="cart-validation-message">We've removed the following ${removedItems.length} item${removedItems.length === 1 ? '' : 's'} because you already own ${removedItems.length === 1 ? 'it' : 'them'}:</p>
          
          <div class="cart-validation-removed-items">
            ${itemsList}
          </div>
          
          <div class="cart-validation-actions">
            <button class="cart-validation-btn cart-validation-btn--primary" onclick="window.location.href='${checkoutUrl}'">
              Continue to Checkout
            </button>
            <button class="cart-validation-btn cart-validation-btn--secondary" onclick="window.cartValidationManager.handleReviewCart()">
              Review Cart
            </button>
          </div>
        </div>
      </div>
    `;

    return overlay;
  }

  /**
   * Start progress sequence animation
   */
  startProgressSequence(overlay) {
    const progressSteps = [
      'Scanning cart items...',
      'Checking your order history...',
      'Removing duplicate items...',
      'Updating cart...',
      'Preparing checkout...'
    ];

    const progressElement = overlay.querySelector('.cart-validation-progress');
    let currentStep = 0;

    const updateProgress = () => {
      if (currentStep < progressSteps.length) {
        progressElement.textContent = progressSteps[currentStep];
        currentStep++;
        setTimeout(updateProgress, 800);
      }
    };

    setTimeout(updateProgress, 800);
  }

  /**
   * Build message for removal notification
   */
  buildRemovalMessage(removedItems) {
    const directPurchases = removedItems.filter(item => item.reason === 'already purchased');
    const packInclusions = removedItems.filter(item => item.reason === 'part of purchased pack');

    if (directPurchases.length > 0 && packInclusions.length > 0) {
      return `${removedItems.length} items were removed from your cart: ${directPurchases.length} you already own directly, and ${packInclusions.length} are included in packs you've purchased.`;
    } else if (directPurchases.length > 0) {
      if (directPurchases.length === 1) {
        return `"${directPurchases[0].title}" was removed from your cart because you already own this item.`;
      } else {
        const itemNames = directPurchases.map(item => item.title).join(', ');
        return `${directPurchases.length} items were removed from your cart because you already own them: ${itemNames}`;
      }
    } else if (packInclusions.length > 0) {
      if (packInclusions.length === 1) {
        return `"${packInclusions[0].title}" was removed from your cart because it's included in a pack you've already purchased.`;
      } else {
        const itemNames = packInclusions.map(item => item.title).join(', ');
        return `${packInclusions.length} items were removed from your cart because they're included in packs you've already purchased: ${itemNames}`;
      }
    }

    return `${removedItems.length} items were removed from your cart.`;
  }

  /**
   * Build detailed list of removed items for checkout summary
   */
  buildDetailedItemsList(removedItems) {
    return removedItems.map(item => {
      const reasonIcon = item.reason === 'already purchased' ? 
        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a7f37" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>` : 
        `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff3da1" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <path d="M9 9h6m-6 4h6"/>
        </svg>`;

      const reasonText = item.reason === 'already purchased' ? 
        'You already own this item' : 
        'Included in a pack you own';

      return `
        <div class="cart-validation-removed-item">
          <div class="cart-validation-removed-item-info">
            <span class="cart-validation-removed-item-title">${item.title}</span>
            <div class="cart-validation-removed-item-reason">
              ${reasonIcon}
              <span>${reasonText}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Remove notification from DOM
   */
  removeNotification(notification) {
    if (this.activeNotifications.has(notification)) {
      notification.style.animation = 'slideOutRight 0.3s ease-in forwards';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
          this.activeNotifications.delete(notification);
        }
      }, 300);
    }
  }

  /**
   * Inject required CSS styles
   */
  injectStyles() {
    if (this.styleInjected) return;

    const styleId = 'cart-validation-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = this.getStyles();
    document.head.appendChild(style);
    
    this.styleInjected = true;
  }

  /**
   * Get CSS styles for UI components
   */
  getStyles() {
    return `
      .cart-validation-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: ${this.config.theme.fontFamily};
      }

      .cart-validation-modal {
        background: #fff;
        padding: 40px;
        border-radius: 12px;
        box-shadow: 0 12px 32px rgba(0,0,0,0.1);
        text-align: center;
        max-width: 480px;
        width: 90%;
        border-left: 4px solid ${this.config.theme.primaryColor};
      }

      .cart-validation-content {
        /* Content styling handled by individual elements */
      }

      .cart-validation-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 24px;
      }

      .cart-validation-icon--loading {
        background: linear-gradient(45deg, ${this.config.theme.primaryColor}, #f466af);
        animation: pulse 2s infinite;
      }

      .cart-validation-icon--success {
        background: ${this.config.theme.successColor};
      }

      .cart-validation-icon--warning {
        background: #f59e0b;
      }

      .cart-validation-title {
        margin: 0 0 16px;
        font-size: 24px;
        font-weight: 600;
        color: #333;
      }

      .cart-validation-message {
        margin: 0 0 8px;
        color: #666;
        font-size: 16px;
        line-height: 1.5;
      }

      .cart-validation-submessage {
        margin: 0;
        color: #999;
        font-size: 14px;
      }

      .cart-validation-progress {
        margin-top: 24px;
        font-size: 14px;
        color: ${this.config.theme.primaryColor};
        font-weight: 500;
      }

      .cart-validation-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #fff;
        color: #333;
        padding: 20px 25px;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 450px;
        font-family: ${this.config.theme.fontFamily};
        font-size: 14px;
        line-height: 1.5;
        border-left: 4px solid ${this.config.theme.primaryColor};
        animation: slideInRight 0.3s ease-out;
      }

      .cart-validation-notification-content {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .cart-validation-notification-icon {
        width: 24px;
        height: 24px;
        background: ${this.config.theme.primaryColor};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .cart-validation-notification-body {
        flex: 1;
      }

      .cart-validation-notification-title {
        font-weight: 600;
        margin-bottom: 4px;
        color: ${this.config.theme.primaryColor};
      }

      .cart-validation-notification-message {
        color: #666;
      }

      .cart-validation-notification-close {
        background: none;
        border: none;
        color: #999;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        margin-left: 8px;
        line-height: 1;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
      }

      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }

      @keyframes slideOutRight {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }

      .cart-validation-modal--checkout {
        max-width: 600px;
        text-align: left;
      }

      .cart-validation-removed-items {
        margin: 24px 0;
        max-height: 300px;
        overflow-y: auto;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        background: #f9fafb;
      }

      .cart-validation-removed-item {
        padding: 16px;
        border-bottom: 1px solid #e5e7eb;
      }

      .cart-validation-removed-item:last-child {
        border-bottom: none;
      }

      .cart-validation-removed-item-info {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .cart-validation-removed-item-title {
        font-weight: 600;
        color: #374151;
        font-size: 15px;
      }

      .cart-validation-removed-item-reason {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #6b7280;
      }

      .cart-validation-actions {
        display: flex;
        gap: 12px;
        justify-content: center;
        margin-top: 32px;
      }

      .cart-validation-btn {
        padding: 12px 24px;
        border-radius: 6px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        min-width: 140px;
      }

      .cart-validation-btn--primary {
        background: ${this.config.theme.primaryColor};
        color: white;
      }

      .cart-validation-btn--primary:hover {
        background: #e935a1;
        transform: translateY(-1px);
      }

      .cart-validation-btn--secondary {
        background: #f3f4f6;
        color: #374151;
        border: 1px solid #d1d5db;
      }

      .cart-validation-btn--secondary:hover {
        background: #e5e7eb;
        transform: translateY(-1px);
      }
    `;
  }

  /**
   * Debug logging
   */
  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[CartValidationUI] ${message}`, data || '');
    }
  }
}

// Export for use in other modules
window.CartValidationUI = CartValidationUI; 